import { graphGet } from "../graph/client";
import { SCOPES } from "../auth/config";
import {
  detailsAus,
  ordneZu,
  type AufgabenDetails,
  type PlannerBucket,
  type PlannerTask,
  type PlannerTaskDetails,
  type Zeitplan,
} from "./planner";

// Holt ein Board aus Planner und ordnet es zu. Der einzige Ort, an dem die
// drei Planner-Aufrufe stehen.

export type Board = { id: string; title: string };
type Liste<T> = { value: T[] };

// Nur die Felder anfordern, die planner.ts liest – weniger Daten. Die Liste
// ist gegen den Typ geprüft: Wird dort ein Feld umbenannt, bricht hier der
// Build, statt dass Graph still ein leeres Feld liefert.
const TASK_FELDER = (
  [
    "id",
    "title",
    "bucketId",
    "startDateTime",
    "dueDateTime",
    "percentComplete",
    "priority",
    "assignments",
    "orderHint",
  ] satisfies (keyof PlannerTask)[]
).join(",");

export async function ladeBoard(planId: string): Promise<Board> {
  return graphGet<Board>(`/planner/plans/${planId}`, SCOPES.planner);
}

export async function ladeZeitplan(planId: string): Promise<Zeitplan> {
  const [buckets, tasks] = await Promise.all([
    graphGet<Liste<PlannerBucket>>(`/planner/plans/${planId}/buckets`, SCOPES.planner),
    graphGet<Liste<PlannerTask>>(`/planner/plans/${planId}/tasks?$select=${TASK_FELDER}`, SCOPES.planner),
  ]);
  return ordneZu(buckets.value, tasks.value);
}

/** Alle Boards, die die angemeldete Person sehen darf – für die Auswahl. */
export async function ladeMeineBoards(): Promise<Board[]> {
  const liste = await graphGet<Liste<Board>>("/me/planner/plans", SCOPES.planner);
  return liste.value;
}

/** Die Boards einer Gruppe (= eines Teams) – für die Tab-Einrichtung. */
export async function ladeBoardsDerGruppe(gruppenId: string): Promise<Board[]> {
  const liste = await graphGet<Liste<Board>>(`/groups/${gruppenId}/planner/plans`, SCOPES.planner);
  return liste.value;
}

// ── Aufgaben-Details, erst beim Anklicken ────────────────────────────────────

/**
 * Notizen und Checkliste einer Aufgabe.
 *
 * Bewusst OHNE Zwischenspeicher: Der Inhalt ändert sich, sobald jemand in
 * Planner einen Haken setzt. Ein Klick kostet eine kleine Anfrage – das ist
 * billiger als ein Speicher, der veraltete Haken zeigt.
 *
 * Kein $select: Ob dieser Pfad es unterstützt, ist nicht belegt, und die
 * Antwort ist ohnehin klein. Lieber nichts behaupten, was nicht geprüft ist.
 */
export async function ladeAufgabenDetails(taskId: string): Promise<AufgabenDetails> {
  const roh = await graphGet<PlannerTaskDetails>(`/planner/tasks/${taskId}/details`, SCOPES.planner);
  return detailsAus(roh);
}

export type Person = { id: string; name: string };

type GraphNutzer = {
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
};

/**
 * Namen bleiben gemerkt – sie ändern sich nicht, und dieselben Personen
 * stehen an vielen Aufgaben.
 *
 * Gemerkt werden NUR Erfolge. Ein vorübergehender Fehler (Netz, Drosselung)
 * darf keinen Ersatznamen festschreiben, der dann für immer stehen bliebe.
 */
const namen = new Map<string, string>();

/** Wenn gar nichts zu holen ist: die Kennung gekürzt, damit man sie wiedererkennt. */
function kurzeKennung(id: string): string {
  return id.slice(0, 8) + "…";
}

/**
 * Die Namen zu den Bearbeiter-Kennungen.
 *
 * Bewusst mit den Planner-Rechten geholt: Dieses Bündel enthält bereits
 * User.ReadBasic.All und ist in Teams erprobt. Ein eigenes Bündel wäre
 * sauberer, würde aber eine neue Token-Anforderung auslösen, deren stilles
 * Durchgehen unter NAA wir nicht belegt haben.
 *
 * Scheitert eine einzelne Person, bleibt es bei ihrer gekürzten Kennung – der
 * Dialog darf daran nicht kippen. Bei B2B-Gästen ist displayName bekanntlich
 * null; dann greift die Mailadresse.
 */
export async function ladeNamen(ids: readonly string[]): Promise<Person[]> {
  return Promise.all(
    ids.map(async (id) => {
      const gemerkt = namen.get(id);
      if (gemerkt !== undefined) return { id, name: gemerkt };
      try {
        const nutzer = await graphGet<GraphNutzer>(
          `/users/${id}?$select=displayName,mail,userPrincipalName`,
          SCOPES.planner,
        );
        const name = nutzer.displayName ?? nutzer.mail ?? nutzer.userPrincipalName ?? kurzeKennung(id);
        namen.set(id, name);
        return { id, name };
      } catch {
        return { id, name: kurzeKennung(id) };
      }
    }),
  );
}
